import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ProtoApiService, ProtoResponse } from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { Error, SortDirection } from '@lsq-frontend/remote-services/all-java/common-mk0';
import {
    ListInvoiceSummaryForBuyerAccountRequest2,
    mapBuyerPayableSortColumn,
    mapBuyerStatusFiltersToPayableLegacyStatusInput,
    PayablesInput
} from '@lsq-frontend/remote-services/financing-graphql';
import { FeatureFlagService } from '@lsq-frontend/common-ui';
import { EnumType, jsonToGraphQLQuery } from 'json-to-graphql-query';
import { isNil, omitBy } from 'lodash';
import { startWith } from 'rxjs/operators';
import { IExportRequest } from '../models/export-request';
import { ExportType } from '../models/export-type';

interface IGetBuyerAccountInvoiceExportResponse {
    ok: boolean;
    fileString: string;
    error: Error;
}

export class GetBuyerAccountInvoiceExportResponse
    extends ProtoResponse
    implements IGetBuyerAccountInvoiceExportResponse
{
    public ok = false;
    public fileString = '';
    public error: Error = null;

    constructor(obj?: Partial<IGetBuyerAccountInvoiceExportResponse>) {
        super();
        this.ok = obj?.ok ?? this.ok;
        this.fileString = obj?.fileString ?? this.fileString;
        this.error = obj?.error ?? this.error;
    }
}

@Injectable({ providedIn: 'root' })
export class GetBuyerAccountInvoiceExportService extends ProtoApiService<
    ListInvoiceSummaryForBuyerAccountRequest2,
    GetBuyerAccountInvoiceExportResponse
> {
    public requestType = ListInvoiceSummaryForBuyerAccountRequest2;
    public responseType = GetBuyerAccountInvoiceExportResponse;
    public endpointName = 'GetBuyerAccountInvoiceExport';
    public serviceName = 'financing';

    private _isPayLaterEnabled = false;

    constructor(
        http: HttpClient,
        @Inject(SETTINGS) settings: ISettings,
        private _featureFlagService: FeatureFlagService
    ) {
        super(http, settings);

        this._featureFlagService
            .isOn(FeatureFlags.PayLater)
            .pipe(startWith(this._featureFlagService.isOnSync(FeatureFlags.PayLater)))
            .subscribe((isOn) => {
                this._isPayLaterEnabled = isOn;
                if (this._isPayLaterEnabled) {
                    this.endpointName = 'v2/financingExport';
                    this.serviceName = 'reportCenter';
                    this.usePascalForUrl = false;
                } else {
                    this.endpointName = 'GetBuyerAccountInvoiceExport';
                    this.serviceName = 'financing';
                    this.usePascalForUrl = true;
                }
            });
    }

    public requestMapper(
        req: ListInvoiceSummaryForBuyerAccountRequest2
    ): IExportRequest | ListInvoiceSummaryForBuyerAccountRequest2 {
        if (this._isPayLaterEnabled) {
            const {
                skip,
                take,
                sortColumn,
                sortDirection,
                secondarySortColumn,
                secondarySortDirection
            } = req.pagination;

            const input: PayablesInput = omitBy(
                {
                    legacyStatuses: mapBuyerStatusFiltersToPayableLegacyStatusInput(
                        req.buyerInvoiceStatus
                    ),
                    companyFilter: {
                        buyerIds: req.buyerKeys,
                        supplierIds: req.sellerKeys ?? []
                    },
                    payableNameSearch: req.invoiceNumberFilter ?? '',
                    pagination: {
                        skip: skip || 0,
                        take: take || 0,
                        sortColumn: new EnumType(mapBuyerPayableSortColumn(sortColumn)),
                        sortDirection: new EnumType(
                            SortDirection[sortDirection] || SortDirection.ASCENDING
                        ),
                        secondarySortColumn: new EnumType(
                            mapBuyerPayableSortColumn(secondarySortColumn)
                        ),
                        secondarySortDirection: new EnumType(
                            SortDirection[secondarySortDirection] || SortDirection.ASCENDING
                        )
                    }
                },
                isNil
            );
            const money = { cents: true, currencyCode: true };
            const query = jsonToGraphQLQuery({
                payables: {
                    __args: { input },
                    rows: {
                        name: true,
                        status: true,
                        statusLegacy: true,
                        fundingDate: true,
                        originalDueDate: true,
                        payLaterAdjustedDueDate: true,
                        isGroup: true,
                        feesDue: money,
                        relationship: {
                            supplier: {
                                id: true,
                                company: { name: true },
                                platformExternalId: true
                            },
                            buyer: { id: true, company: { name: true } }
                        },
                        approvals: {
                            payLaterRequested: true,
                            payLaterAdjustedDueDate: true,
                            originalDueDate: true,
                            amount: money,
                            invoice: {
                                date: true,
                                invoiceNumber: true
                            },
                            creditMemoAllocations: {
                                allocationType: true
                            }
                        },
                        creditMemos: {
                            date: true,
                            creditMemoNumber: true,
                            allocatedAmount: money
                        }
                    }
                }
            });

            return {
                fileType: 'csv',
                exportType: ExportType.BUYER_ACCOUNT_INVOICES,
                query: `{${query}}`
            };
        }
        return req;
    }

    public get endpointUrl(): string {
        return this._isPayLaterEnabled ? super.endpointUrl.replace('/api', '') : super.endpointUrl;
    }
}
